import { PieceJointeBar } from '@mairistem/comptabilite';

import { Fiche, FicheContainerContext } from '@mairistem/fiche';
import type { ErrorField } from '@mairistem/fiche/lib/ts/types';
import {
	Fichiers,
	getNatureFromGabarit,
	TYPE_GABARIT,
	useGabaritPj,
} from '@mairistem/finances-utils';
import React from 'react';
import Commentaires from '@/src/components/Common/Commentaires';
import EcrituresComptables from '@/src/components/Common/EcrituresComptables/EcrituresComptables';
import ConditionsVersements from '@/src/components/SubventionFichePanel/ConditionsVersements/ConditionsVersements';
import SubventionAttribution from '@/src/components/SubventionFichePanel/Generalite/SubventionAttribution/SubventionAttribution';
import SubventionInformation from '@/src/components/SubventionFichePanel/Generalite/SubventionInformation/SubventionInformation';
import SubventionListeContainer from '@/src/components/SubventionListe/SubventionListeContainer';
import type { Composant } from '@/src/interfaces/composant';
import type { Subvention } from '@/src/interfaces/subvention';
import { getMontantDepensesEligibles } from '@/src/utils/subvention';

interface SubventionFicheConsumerProps {
	identifiant: string | number;
	subvention?: Subvention;
	errors?: Array<ErrorField>;
	onChange: any;
}

const SubventionFicheConsumer = ({
	identifiant,
	subvention,
	onChange,
	errors = null,
	...rest
}: SubventionFicheConsumerProps) => {
	const {
		financeur,
		libelle,
		identifiantEntite,
		projet,
		referenceDossier,
		dateDebut,
		mntAttribue,
		etiquettes,
		etiquettesProjet,
		cloture,
		realisationHT,
		plafondEligible,
		tauxAide,
		montantEnvisage,
		assietteEligible,
		composants,
		deletedDocuments,
	} = subvention || {};

	const context = React.useContext(FicheContainerContext);
	const [, gabaritsPj] = useGabaritPj([
		TYPE_GABARIT.SUBVENTION_PERCUE,
		TYPE_GABARIT.PROJET,
	]);

	const handleChangePj = React.useCallback(
		(changes) => {
			onChange({
				identifiant,
				...changes,
			});
		},
		[identifiant, onChange],
	);

	const handleDeletePj = React.useCallback(
		(identifiantPj: number) => {
			onChange?.({
				identifiant,
				deletedDocuments: [...(deletedDocuments || []), identifiantPj],
			});
		},
		[identifiant, onChange, deletedDocuments],
	);

	const information = React.useMemo(
		() => (
			<Fiche.Panel key="generalite" id="generalite" title="GÉNÉRALITÉS">
				<SubventionInformation
					identifiant={identifiant}
					financeur={financeur}
					libelle={libelle}
					identifiantEntite={identifiantEntite}
					projet={projet}
					etiquettes={etiquettes}
					etiquettesProjet={etiquettesProjet}
					isCloture={cloture}
					realisationHT={realisationHT}
					plafondEligible={plafondEligible}
					tauxAide={tauxAide}
					montantEnvisage={montantEnvisage}
					assietteEligible={assietteEligible}
					errors={errors}
					onChange={onChange}
					composants={composants}
				/>
			</Fiche.Panel>
		),
		[
			financeur,
			libelle,
			identifiantEntite,
			projet,
			etiquettes,
			etiquettesProjet,
			cloture,
			identifiant,
			realisationHT,
			errors,
			composants,
			montantEnvisage,
			assietteEligible,
			plafondEligible,
			tauxAide,
			onChange,
		],
	);

	const attribution = React.useMemo(
		() => (
			<Fiche.Panel key="attribution" id="attribution" title="ATTRIBUTION">
				<SubventionAttribution
					identifiant={identifiant}
					referenceDossier={referenceDossier}
					dateDebut={dateDebut}
					mntAttribue={mntAttribue}
					conditionsVersements={subvention?.conditionsVersements}
					errors={errors}
					onChange={onChange}
				/>
			</Fiche.Panel>
		),
		[
			identifiant,
			referenceDossier,
			dateDebut,
			mntAttribue,
			subvention?.conditionsVersements,
			errors,
			onChange,
		],
	);

	const conditionsVersements = React.useMemo(
		() => (
			<Fiche.Panel
				key="conditions"
				id="conditions"
				title="CONDITIONS DE VERSEMENT"
			>
				<ConditionsVersements
					conditionsVersements={subvention?.conditionsVersements}
					subvention={subvention}
					montantSubvention={subvention?.mntAttribue}
					onChange={onChange}
					identifiant={subvention?.identifiant}
					identifiantEntite={identifiantEntite}
					deletedConditionsVersements={subvention?.deletedConditionsVersements}
					gabaritsPj={gabaritsPj}
				/>
			</Fiche.Panel>
		),
		[identifiantEntite, subvention, onChange, gabaritsPj],
	);

	const montantDepenseEligibles = getMontantDepensesEligibles(
		composants as Composant[],
		realisationHT,
	);

	const ecrituresComptables = React.useMemo(
		() => (
			<Fiche.Panel key="ecritures" id="ecritures" title="ECRITURES COMPTABLES">
				<EcrituresComptables
					projet={subvention?.projet}
					conditionsVersements={subvention?.conditionsVersements}
					montantSubvention={subvention?.mntAttribue}
					realisationHT={realisationHT}
					composants={composants as Composant[]}
					montantProjet={montantDepenseEligibles}
				/>
			</Fiche.Panel>
		),
		[
			subvention?.projet,
			subvention?.conditionsVersements,
			subvention?.mntAttribue,
			realisationHT,
			composants,
			montantDepenseEligibles,
		],
	);

	const commentaires = React.useMemo(
		() => (
			<Fiche.Panel key="commentaires" id="commentaires" title="COMMENTAIRES">
				<Commentaires
					identifiant={subvention?.identifiant}
					commentaire={subvention?.commentaire}
					infosPubliques={subvention?.infosPubliques}
					onChange={onChange}
				/>
			</Fiche.Panel>
		),
		[
			subvention?.identifiant,
			onChange,
			subvention?.commentaire,
			subvention?.infosPubliques,
		],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Pas besoin de 'handleDeletePj'
	const getPjSideBar = React.useCallback(
		() => (
			<PieceJointeBar
				identifiantFichierLie={identifiant}
				nomFichierLie={Fichiers.SUBVENTION_PERCUE}
				types={getNatureFromGabarit(
					gabaritsPj?.find((g) => g.type === TYPE_GABARIT.SUBVENTION_PERCUE),
				)}
				onChange={handleChangePj}
				onDeletePj={handleDeletePj}
				typeGabarit={TYPE_GABARIT.SUBVENTION_PERCUE}
			/>
		),
		[identifiant, handleChangePj, gabaritsPj],
	);

	let header = '';

	if (context.status.created && !context.status.updated) {
		header = 'Nouvelle subvention';
	} else {
		header = `${subvention?.libelle || ''}`;
	}

	const sidebars = React.useMemo(
		() => ({
			left: <SubventionListeContainer />,
			right: {
				content: getPjSideBar(),
				icon: 'attach',
				alwaysPinned: true,
				width: '15vw',
			},
		}),
		[getPjSideBar],
	);

	return (
		<span className="ficheWithSideBar">
			<Fiche
				type="subvention"
				header={header}
				errors={errors}
				sidebars={sidebars}
				{...rest}
			>
				{information}
				{attribution}
				{conditionsVersements}
				{ecrituresComptables}
				{commentaires}
			</Fiche>
		</span>
	);
};

export default SubventionFicheConsumer;
